import { CardinalEvents, CardinalPaymentBrand, CybersourceResponseCodes, TwoFAEvents } from './constants';
import { Emitter } from 'nanoevents';
import { CheckEnroll, CheckEnrollResponse, ValidationOrderData } from './interface';
import { validate } from '../../http/threeDS';
import { logError } from 'app/util/errorsLoggerWrapper';
import { ThreeDSStep, ThreeDSTracer } from './trace';

export const createAdditionalOrderData = (
  checkoutAffiliation: string,
  serviceAcquirer: string,
  authorizationTfa: CheckEnroll,
): ValidationOrderData => {
  const {
    cavv,
    eci,
    veresEnrolled,
    xID,
    transactionId,
    specificationVersion,
    reasonCode,
    directoryServerTransactionId,
  } = authorizationTfa;

  return {
    checkoutAffiliation,
    serviceAcquirer,
    TwoFactorAuthentication: {
      cavv,
      eci,
      xid: xID,
      status: veresEnrolled,
      transactionId,
      version: specificationVersion,
      reasonCode,
      directoryServerTransactionId,
    },
  };
};

export const emitError = (emitter: Emitter, source: string, data: any, tracer?: ThreeDSTracer) => {
  // ERROR_EMITTED, not EXECUTE_REJECTED: this is the cause, and the promise rejection that
  // follows is recorded separately by execute(). Keeping them apart makes the trace read in
  // the order things actually happened.
  tracer?.fail(ThreeDSStep.ERROR_EMITTED, { error_source: source }, data);
  emitter.emit(TwoFAEvents.ERROR, { source, data });
};

/**
 * Ends the flow the same way emitError does, but records it as what it is: the cardholder or
 * the issuer refused. Warns instead of erroring, so an expected decline never lands in Error
 * Tracking next to the failures that need someone to look at them.
 */
export const emitDecline = (emitter: Emitter, source: string, data: any, tracer?: ThreeDSTracer) => {
  tracer?.decline(ThreeDSStep.AUTHENTICATION_DECLINED, { error_source: source }, data);
  emitter.emit(TwoFAEvents.ERROR, { source, data });
};

export const checkIfRequiresChallenge = (
  emitter: Emitter,
  checkEnroll: CheckEnroll,
  tracer?: ThreeDSTracer,
): boolean => {
  if (!checkEnroll.reasonCode) {
    emitError(emitter, 'reasonCode in checkEnroll is null', checkEnroll, tracer);
  }
  return checkEnroll.reasonCode === CybersourceResponseCodes.DAUTHENTICATE;
};

export const processChallenge = async (
  cardinal: any,
  affiliation: string,
  serviceAcquirer: string,
  checkEnrollData: CheckEnroll,
  emitter: Emitter,
  requireChallenge: boolean,
  tracer?: ThreeDSTracer,
) => {
  try {
    if (!requireChallenge) {
      /**
       * This may seem like an error since they would be passing error codes other than 100, e.g. 101, 102, etc;
       * We will process error codes and successful validation code equally since in both cases we must call
       * capture with the chekEnroll data to save the result information, the capture service will save the data in both
       * cases but will throw an error when an error code arrives, because the checkEnroll
       * response will be incomplete and will not go to the bank.
       */
      handleResponseWithoutChallenge(emitter, affiliation, serviceAcquirer, checkEnrollData, tracer);
    } else {
      handleRequireChallenge(cardinal, emitter, checkEnrollData, tracer);
    }
  } catch (e) {
    emitError(emitter, 'handleSetupComplete: Unexpected error occurred', e, tracer);
  }
};

const handleRequireChallenge = (
  cardinal: any,
  emitter: Emitter,
  checkEnrollData: CheckEnroll,
  tracer?: ThreeDSTracer,
) => {
  const { acsURL, authenticationTransactionId, paReq } = checkEnrollData;
  // Truthy, not a null check: the backend types these as `string | null` and an empty string
  // sails past `== null`, reaching cardinal.continue with an empty AcsUrl. Songbird then
  // mounts a frame pointing nowhere, never emits payments.validated, and the flow stalls
  // with no trace of why.
  const checkEnrollResponseIsIncomplete = !acsURL || !paReq || !authenticationTransactionId;

  if (checkEnrollResponseIsIncomplete) {
    tracer?.fail(ThreeDSStep.CHALLENGE_DATA_INCOMPLETE, {
      has_acs_url: !!acsURL,
      has_pareq: !!paReq,
      has_auth_transaction_id: !!authenticationTransactionId,
    });
    emitError(emitter, 'handleSetupComplete: args of object response are incomplete', checkEnrollData, tracer);
    return;
  }
  tracer?.enrich({ authTransactionId: authenticationTransactionId });
  tracer?.step(ThreeDSStep.CHALLENGE_REQUESTED, { acs_url_host: safeHost(acsURL) });
  handle3DSChallenge(cardinal, acsURL, paReq, authenticationTransactionId);
};

const safeHost = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return 'unparseable';
  }
};

const handleResponseWithoutChallenge = (
  emitter: Emitter,
  affiliation: string,
  serviceAcquirer: string,
  checkEnrollData: CheckEnroll,
  tracer?: ThreeDSTracer,
) => {
  tracer?.step(ThreeDSStep.FRICTIONLESS_RESOLVED, describeAuthentication(checkEnrollData));
  tracer?.step(ThreeDSStep.ORDER_DATA_EMITTED);
  emitter.emit(TwoFAEvents.CREATE_ORDER, createAdditionalOrderData(affiliation, serviceAcquirer, checkEnrollData));
};

/**
 * Presence and status codes only. The CAVV is a cryptogram and the ECI/xID identify the
 * authentication -- log that they arrived, never what they are.
 */
export const describeAuthentication = (data: CheckEnroll) => ({
  reason_code: data?.reasonCode ?? null,
  veres_enrolled: data?.veresEnrolled ?? null,
  eci: data?.eci ?? null,
  has_cavv: !!data?.cavv,
  has_xid: !!data?.xID,
  has_ds_transaction_id: !!data?.directoryServerTransactionId,
  specification_version: data?.specificationVersion ?? null,
});

const handle3DSChallenge = (cardinal: any, acsURL: string, paReq: string, authenticationTransactionId: string) => {
  cardinal.continue(
    CardinalPaymentBrand.CCA,
    { AcsUrl: acsURL, Payload: paReq },
    { OrderDetails: { TransactionId: authenticationTransactionId } },
  );
};

export const handlePaymentValidated = async (
  validationResult: any,
  cardToValidate: any,
  affiliation: string,
  serviceAcquirer: string,
  emitter: Emitter,
  tracer?: ThreeDSTracer,
) => {
  try {
    const { Payment } = validationResult;
    // Note: this does not gate on validationResult.Validated, so a failed authentication that
    // still carries a ProcessorTransactionId goes on to validate() and resolves as a success.
    // Tracing it here rather than changing the behaviour, so the size of that population is
    // measurable before anything starts declining on it.
    const cardinalDetails = {
      validated: validationResult?.Validated ?? null,
      action_code: validationResult?.ActionCode ?? null,
      error_number: validationResult?.ErrorNumber ?? null,
      has_processor_transaction_id: !!Payment?.ProcessorTransactionId,
    };
    // In live mode this is the only place a failed challenge shows up, since the sandbox
    // fallback that catches it in test is not registered there. Recorded as a decline rather
    // than a plain step, without changing what happens next.
    if (validationResult?.Validated === false) {
      tracer?.decline(ThreeDSStep.CARDINAL_VALIDATED, cardinalDetails);
    } else {
      tracer?.step(ThreeDSStep.CARDINAL_VALIDATED, cardinalDetails);
    }
    // Not optional-chained, and that is load-bearing: a missing Payment throws here, which is
    // what stops a validate() call that could not carry an id. A Payment present but without a
    // ProcessorTransactionId still goes through, per the cases in challengeHelpers.test.
    const processorTransactionId = Payment.ProcessorTransactionId;
    // Compared against the id check-enroll returned, which is the only other copy: an earlier
    // version compared it to cardToValidate.authenticationTransactionID *after* assigning
    // this same value into it, so it could only ever report true. cardToValidate starts that
    // field at 0 (see getCardinalInitialData) and never holds the enrolled id.
    //
    // If these always match, validate() can be replayed from check-enroll data alone, with no
    // browser involved -- which is what reconciling a stalled flow server-side would need.
    // The raw id goes out too, so the claim stays auditable instead of resting on a boolean.
    const enrolledTransactionId = tracer?.getContext().authTransactionId ?? null;
    cardToValidate.authenticationTransactionID = processorTransactionId;
    tracer?.step(ThreeDSStep.VALIDATE_REQUEST, {
      processor_transaction_id: processorTransactionId ?? null,
      enrolled_transaction_id: enrolledTransactionId,
      echoes_auth_transaction_id: !!enrolledTransactionId && processorTransactionId === enrolledTransactionId,
    });
    const validateData: CheckEnrollResponse = await validate({ data: cardToValidate }); // check if the validateResponse really is the same object as checkEnroll service
    tracer?.step(ThreeDSStep.VALIDATE_RESPONSE, describeAuthentication(validateData?.objectResponse));
    tracer?.step(ThreeDSStep.ORDER_DATA_EMITTED);
    emitter.emit(
      TwoFAEvents.CREATE_ORDER,
      createAdditionalOrderData(affiliation, serviceAcquirer, validateData.objectResponse),
    );
  } catch (e) {
    emitError(emitter, 'handlePaymentValidated: Unexpected error occurred', e, tracer);
  }
};

/**
 * Returns its own detach so teardown can name the handler. window.Cardinal is a singleton and
 * `off(event)` with no reference drops every listener on that event, including one belonging
 * to a flow still waiting for its result.
 */
export const setupValidateListener = (
  cardinal: any,
  cardToValidate: any,
  localEmitter: any,
  affiliation: string,
  acquirer: string,
  tracer?: ThreeDSTracer,
): (() => void) => {
  const onValidated = (validationResult: any) =>
    handlePaymentValidated(validationResult, cardToValidate, affiliation, acquirer, localEmitter, tracer);
  cardinal.on(CardinalEvents.VALIDATED, onValidated);
  return () => {
    try {
      cardinal.off(CardinalEvents.VALIDATED, onValidated);
    } catch (e) {
      logError('3DS: could not detach the validate listener', { message: (e as Error)?.message });
    }
  };
};

const TEST_CCA_ORIGIN = 'https://cas.client.cardinaltrusted.com';

const decodeJwtPayload = (jwt: string) => JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));

// A single incoming CCA postMessage is broadcast to every listener attached to window, not
// just the most recent one. If execute() is invoked again before a prior call's cleanup()
// runs (see CRD-2124 for the same race on CardinalEvents.VALIDATED), more than one of these
// listeners can be live at once. Dedupe by the transaction id carried in the message itself
// so a single incoming response never triggers two real /api/3ds/validate calls.
//
// Known accepted limitation: this only fixes "both listeners fire". It does NOT guarantee
// the *correct* flow is the one that wins — window.Cardinal is a singleton, so a second
// execute()'s cardinal.continue('cca', ...) supersedes the first flow's challenge in the
// DOM, meaning the arriving message actually belongs to the newest flow, while this dedupe
// lets whichever listener is registered first claim it. Under a genuine concurrent-retry
// race this can still pair one flow's authentication result with a different flow's
// cardToValidate, or leave a flow's orderPromise unsettled. The correct fix is to have each
// listener only act on messages matching its own flow's authenticationTransactionId (known
// once checkEnroll resolves), not a first-to-claim global dedupe — left as follow-up work,
// tracked alongside the rest of this workaround's removal once Cardinal fixes their side.
const handledCcaProcessorTransactionIds = new Set<string>();

/**
 * Cardinal's sandbox serves the CCA TermURL response from cas.client.cardinaltrusted.com,
 * an origin missing from the midas.postMessageDomains allow list baked into songbird.js.
 * Songbird discards the message, never emits payments.validated, and the challenge frame
 * stays blank. This listener picks up the discarded response so test mode keeps working.
 *
 * It bypasses the JWT signature check songbird would have done, so it must never run in
 * live mode. Remove once Cardinal adds the origin to their allow list.
 *
 * Open question, needs backend confirmation before this can be considered fully safe: the
 * only untrusted field forwarded to the real /api/3ds/validate call is Payment.
 * ProcessorTransactionId (see handlePaymentValidated) — CAVV/ECI/authentication status
 * always come back from validate()'s own response, never from this unverified JWT. Whether
 * that is exploitable depends on whether the backend verifies server-side that the
 * ProcessorTransactionId belongs to the caller's own chargeId/session.
 */
export const setupTestCcaFallback = (
  cardinal: any,
  cardToValidate: any,
  localEmitter: any,
  affiliation: string,
  acquirer: string,
  tracer?: ThreeDSTracer,
): (() => void) => {
  const onMessage = (event: MessageEvent) => {
    if (typeof event.data !== 'string' || event.data.indexOf('CardinalJWT') === -1) return;
    if (event.origin !== TEST_CCA_ORIGIN) {
      logError('setupTestCcaFallback: message with a CardinalJWT arrived from an unexpected origin', {
        origin: event.origin,
      });
      return;
    }
    try {
      const { CardinalJWT } = JSON.parse(event.data);
      const { Payload } = decodeJwtPayload(CardinalJWT);
      if (!Payload?.Validated) {
        emitDecline(
          localEmitter,
          'setupTestCcaFallback: CCA response was not validated',
          {
            ActionCode: Payload?.ActionCode,
            ErrorNumber: Payload?.ErrorNumber,
            ErrorDescription: Payload?.ErrorDescription,
          },
          tracer,
        );
        return;
      }
      const processorTransactionId = Payload?.Payment?.ProcessorTransactionId;
      if (processorTransactionId) {
        if (handledCcaProcessorTransactionIds.has(processorTransactionId)) {
          // Not informational: a duplicate means two listeners were live at once, which is
          // the CRD-2124 race described above.
          logError('setupTestCcaFallback: duplicate CCA response ignored', { processorTransactionId });
          return;
        }
        handledCcaProcessorTransactionIds.add(processorTransactionId);
      }
      try {
        cardinal.trigger(CardinalEvents.UI_CLOSE);
      } catch (e) {
        logError('setupTestCcaFallback: cardinal.trigger(UI_CLOSE) failed', { message: (e as Error)?.message });
      }
      handlePaymentValidated(Payload, cardToValidate, affiliation, acquirer, localEmitter, tracer);
    } catch (e) {
      emitError(
        localEmitter,
        'setupTestCcaFallback: could not parse CCA response',
        { message: (e as Error)?.message },
        tracer,
      );
    }
  };

  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
};
